// client/src/pages/CourierPage.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import BrandFooter from '../components/BrandFooter';
import { useStaffWebPush } from '../hooks/useStaffWebPush';
import { getNotificationApi } from '../utils/safeNotification';
import { logoPng } from '../config/assets';

const STATUS_PRIORITY = {
  ready: 1,
  cooking: 2,
  accepted: 3,
  new: 4,
  on_the_way: 5,
};

const STATUS_CONFIG = {
  new: { label: '🆕 Новый', bg: '#F5F5F5', color: '#666', border: '#E0E0E0' },
  accepted: { label: '✅ Принят', bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
  cooking: { label: '🍳 Готовится', bg: '#FFF3E0', color: '#E65100', border: '#FFCC02' },
  ready: { label: '📦 Готов к выдаче!', bg: '#FFF8E1', color: '#F57F17', border: '#FFD600' },
  on_the_way: { label: '🚗 В пути', bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
};

export default function CourierPage({ token, user, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('active');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(15);

  // ─── Звуковой сигнал при новом заказе ───────────────────────
  const prevOrderIds = useRef(new Set());

  const playBeep = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [0, 0.25, 0.5].forEach(startTime => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.4, ctx.currentTime + startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startTime + 0.2);
        osc.start(ctx.currentTime + startTime);
        osc.stop(ctx.currentTime + startTime + 0.2);
      });
    } catch (e) {
      console.warn('Audio error:', e);
    }
  }, []);

  const api = useMemo(
    () =>
      axios.create({
        headers: { Authorization: `Bearer ${token}` },
      }),
    [token]
  );

  useStaffWebPush({
    enabled:
      !!user?.restaurant_id && ['courier', 'owner'].includes(user?.role),
    api,
    restaurantId: user?.restaurant_id,
    userRole: user?.role,
  });

  const showMsg = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 3000);
  };

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await api.get('/api/courier/orders');
      const sorted = [...(data || [])].sort((a, b) => {
        const pa = STATUS_PRIORITY[a.status] ?? 9;
        const pb = STATUS_PRIORITY[b.status] ?? 9;
        if (pa !== pb) return pa - pb;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setOrders(prev => {
        const newReadyOrders = sorted.filter(
          o => o.status === 'ready' && !prevOrderIds.current.has(o.id)
        );
        if (newReadyOrders.length > 0) {
          playBeep();
          const N = getNotificationApi();
          if (N && N.permission === 'granted') {
            // eslint-disable-next-line new-cap
            new N('📦 Новый заказ готов!', {
              body: newReadyOrders.map(o =>
                `#${o.id} ${o.client_name} — ${Number(o.total).toLocaleString()} сум`
              ).join('\n'),
              icon: logoPng(),
            });
          }
        }
        prevOrderIds.current = new Set(sorted.map(o => o.id));
        return sorted;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [playBeep]); // eslint-disable-line

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/api/courier/history');
      setHistory(data || []);
    } catch (err) {
      console.error(err);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchOrders();
    const timer = setInterval(() => {
      fetchOrders();
      setCountdown(15);
    }, 15000);
    const tick = setInterval(() => {
      setCountdown(c => (c > 0 ? c - 1 : 15));
    }, 1000);
    return () => {
      clearInterval(timer);
      clearInterval(tick);
    };
  }, [fetchOrders]);

  useEffect(() => {
    if (tab === 'history') fetchHistory();
  }, [tab, fetchHistory]);

  const handleTake = async (orderId) => {
    try {
      await api.patch(`/api/courier/orders/${orderId}/take`);
      showMsg('✅ Заказ взят в работу');
      fetchOrders();
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Ошибка'));
    }
  };

  const handleDelivered = async (orderId) => {
    if (!window.confirm('Отметить заказ как доставленный?')) return;
    try {
      await api.patch(`/api/courier/orders/${orderId}/delivered`);
      showMsg('✅ Заказ доставлен!');
      fetchOrders();
      fetchHistory();
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Ошибка'));
    }
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm('Отказаться от заказа? Он вернётся в очередь.')) return;
    try {
      await api.patch(`/api/courier/orders/${orderId}/cancel`);
      showMsg('Заказ возвращён в очередь');
      fetchOrders();
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Ошибка'));
    }
  };

  const getMapsUrl = (address, type = 'yandex') => {
    const enc = encodeURIComponent(address);
    if (type === 'yandex') return `https://yandex.uz/maps/?text=${enc}`;
    if (type === 'google') return `https://www.google.com/maps/search/${enc}`;
    if (type === '2gis') return `https://2gis.uz/tashkent/search/${enc}`;
    return '#';
  };

  const refreshNow = () => {
    fetchOrders();
    setCountdown(15);
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.headerTitle}>🚗 Курьер</div>
          <div style={s.headerSub}>{user?.email}</div>
        </div>
        <button onClick={onLogout} style={s.logoutBtn}>Выйти</button>
      </div>

      {message && <div style={s.message}>{message}</div>}

      <div style={s.tabs}>
        <button
          style={{ ...s.tab, ...(tab === 'active' ? s.tabActive : {}) }}
          onClick={() => setTab('active')}
        >
          📋 Активные
          {orders.length > 0 && <span style={s.badge}>{orders.length}</span>}
        </button>
        <button
          style={{ ...s.tab, ...(tab === 'history' ? s.tabActive : {}) }}
          onClick={() => setTab('history')}
        >
          ✅ Сегодня доставил
          {history.length > 0 && (
            <span style={{ ...s.badge, backgroundColor: '#4CAF50' }}>{history.length}</span>
          )}
        </button>
      </div>

      {tab === 'active' && (
        <div>
          {loading ? (
            <div style={s.empty}>⏳ Загрузка...</div>
          ) : orders.length === 0 ? (
            <div style={s.empty}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉</div>
              <div style={{ fontWeight: '600' }}>Нет активных заказов</div>
              <div style={{ fontSize: '13px', color: '#999', marginTop: '4px' }}>
                Обновляется каждые 15 секунд
              </div>
            </div>
          ) : (
            orders.map(order => (
              <CourierOrderCard
                key={order.id}
                order={order}
                user={user}
                onTake={() => handleTake(order.id)}
                onDelivered={() => handleDelivered(order.id)}
                onCancel={() => handleCancel(order.id)}
                getMapsUrl={getMapsUrl}
              />
            ))
          )}
          {!loading && (
            <div style={s.refreshRow}>
              <div style={{
                width: '8px', height: '8px',
                borderRadius: '50%',
                backgroundColor: countdown <= 3 ? '#4CAF50' : '#E0E0E0',
                transition: 'background-color 0.3s',
                boxShadow: countdown <= 3 ? '0 0 6px #4CAF50' : 'none',
              }} />
              Обновление через {countdown} сек
              <button
                onClick={refreshNow}
                style={s.refreshNowBtn}
              >
                Сейчас
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div>
          {history.length === 0 ? (
            <div style={s.empty}>
              <div style={{ fontSize: '40px', marginBottom: '8px' }}>📭</div>
              <div>Сегодня доставок ещё не было</div>
            </div>
          ) : (
            <>
              <div style={s.daySummary}>
                <div style={s.summaryItem}>
                  <div style={s.summaryNum}>{history.length}</div>
                  <div style={s.summaryLabel}>доставок</div>
                </div>
                <div style={s.summaryItem}>
                  <div style={s.summaryNum}>
                    {history.reduce((sum, o) => sum + Number(o.total), 0).toLocaleString()}
                  </div>
                  <div style={s.summaryLabel}>сум выручка</div>
                </div>
                <div style={s.summaryItem}>
                  <div style={s.summaryNum}>
                    {history.reduce((sum, o) => sum + Number(o.delivery_price), 0).toLocaleString()}
                  </div>
                  <div style={s.summaryLabel}>сум за доставку</div>
                </div>
              </div>
              {history.map(order => (
                <div key={order.id} style={s.historyCard}>
                  <div style={s.historyHeader}>
                    <span style={s.historyId}>#{order.id}</span>
                    <span style={s.historyStatus}>✅ Доставлен</span>
                  </div>
                  <div style={s.historyName}>{order.client_name}</div>
                  {order.address && <div style={s.historyAddr}>📍 {order.address}</div>}
                  <div style={s.historyTotal}>
                    {Number(order.total).toLocaleString()} сум
                    {' · '}
                    {order.payment_method === 'cash' ? '💵 Наличные' :
                     order.payment_method === 'click' ? '📱 Click' : '💳 Payme'}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      <BrandFooter />
    </div>
  );
}

function CourierOrderCard({ order, user, onTake, onDelivered, onCancel, getMapsUrl }) {
  const [mapsOpen, setMapsOpen] = useState(false);
  const isMine = order.courier_id === user?.id;
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.new;
  const isReady = order.status === 'ready';
  const isOnTheWay = order.status === 'on_the_way';

  return (
    <div style={{
      ...cs.card,
      borderLeft: `4px solid ${cfg.border}`,
      animation: isReady ? 'courierPulse 2s infinite' : 'none',
    }}>
      {isReady && (
        <div style={cs.readyBanner}>
          📦 ГОТОВ К ВЫДАЧЕ — ЗАБЕРИ ЗАКАЗ!
        </div>
      )}

      <div style={cs.cardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={cs.orderId}>#{order.id}</span>
          <span style={{
            ...cs.statusBadge,
            backgroundColor: cfg.bg,
            color: cfg.color,
            border: `1px solid ${cfg.border}`,
          }}>
            {cfg.label}
          </span>
        </div>
        <div style={cs.orderTime}>
          {new Date(order.created_at).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>

      <div style={cs.clientBlock}>
        <div style={cs.clientName}>{order.client_name}</div>
        <a href={`tel:${order.client_phone}`} style={cs.callLink}>📞 {order.client_phone}</a>
      </div>

      {order.address && (
        <div style={cs.addressBlock}>
          <div style={cs.addressText}>
            📍 {order.address}
            {order.zone_name && <span style={cs.zoneBadge}>{order.zone_name}</span>}
          </div>
          <div style={{ marginTop: '8px' }}>
            {!mapsOpen ? (
              <button style={cs.mapsBtn} onClick={() => setMapsOpen(true)}>🗺️ Открыть в картах</button>
            ) : (
              <div style={cs.mapsButtons}>
                <a href={getMapsUrl(order.address, 'yandex')} target="_blank" rel="noreferrer" style={cs.mapLink}>🟡 Яндекс</a>
                <a href={getMapsUrl(order.address, 'google')} target="_blank" rel="noreferrer" style={cs.mapLink}>🔵 Google</a>
                <a href={getMapsUrl(order.address, '2gis')} target="_blank" rel="noreferrer" style={cs.mapLink}>🟢 2GIS</a>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={cs.items}>
        {(order.items || []).map((item, i) => (
          <span key={i} style={cs.item}>{item.name} ×{item.quantity}</span>
        ))}
      </div>

      <div style={cs.totalRow}>
        <div style={cs.total}>{Number(order.total).toLocaleString()} сум</div>
        <div style={cs.payment}>
          {order.payment_method === 'cash' ? '💵 Наличные' :
           order.payment_method === 'click' ? '📱 Click' : '💳 Payme'}
        </div>
      </div>

      {order.courier_name_assigned && (
        <div style={cs.courierAssigned}>
          🧑 {isMine ? 'Вы взяли этот заказ' : `Везёт: ${order.courier_name_assigned}`}
        </div>
      )}

      <div style={cs.actions}>
        {!order.courier_id && isReady && (
          <button style={{
            ...cs.btnTake,
            backgroundColor: isReady ? '#FFD600' : '#000',
            color: isReady ? '#000' : '#fff',
          }} onClick={onTake}>
            🚗 Взять в работу
          </button>
        )}
        {isMine && isOnTheWay && (
          <>
            <button style={cs.btnDone} onClick={onDelivered}>✅ Доставил!</button>
            <button style={cs.btnCancel} onClick={onCancel}>🚫 Отказаться</button>
          </>
        )}
        {(order.status === 'new' || order.status === 'accepted') && !isMine && (
          <div style={cs.waitingMsg}>⏳ Ожидаем подтверждения</div>
        )}
        {isOnTheWay && !isMine && order.courier_name_assigned && (
          <div style={cs.waitingMsg}>🚗 Везёт: {order.courier_name_assigned}</div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { maxWidth: '600px', margin: '0 auto', padding: '0 0 32px', minHeight: '100vh', backgroundColor: '#F5F5F5', fontFamily: '-apple-system, sans-serif' },
  header: {
    backgroundColor: '#000', color: '#fff', padding: '16px',
    paddingTop: 'max(20px, calc(12px + env(safe-area-inset-top, 0px)))',
    minHeight: '48px', boxSizing: 'border-box',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  headerTitle: { fontSize: '18px', fontWeight: '800' },
  headerSub: { fontSize: '12px', opacity: 0.6, marginTop: '2px' },
  logoutBtn: { padding: '6px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.3)', backgroundColor: 'transparent', color: '#fff', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' },
  message: { margin: '8px', padding: '12px', borderRadius: '10px', backgroundColor: '#E8F5E9', textAlign: 'center', fontWeight: '500', fontSize: '14px' },
  tabs: { display: 'flex', gap: '8px', padding: '12px' },
  tab: { flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #E0E0E0', backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' },
  tabActive: { backgroundColor: '#000', color: '#fff', border: '1px solid #000' },
  badge: { backgroundColor: '#E85D4A', color: '#fff', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '800' },
  empty: { padding: '60px 20px', textAlign: 'center', color: '#888', fontSize: '15px' },
  refreshRow: {
    textAlign: 'center', padding: '12px', fontSize: '12px', color: '#999',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
  },
  refreshNowBtn: {
    border: 'none', background: 'none', color: '#2196F3', cursor: 'pointer',
    fontSize: '12px', padding: 0, fontFamily: 'inherit',
  },
  daySummary: { margin: '0 12px 8px', backgroundColor: '#000', color: '#fff', borderRadius: '16px', padding: '16px', display: 'flex', justifyContent: 'space-around' },
  summaryItem: { textAlign: 'center' },
  summaryNum: { fontSize: '22px', fontWeight: '800' },
  summaryLabel: { fontSize: '11px', opacity: 0.6, marginTop: '2px' },
  historyCard: { margin: '0 12px 8px', backgroundColor: '#fff', borderRadius: '12px', padding: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  historyHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '6px' },
  historyId: { fontWeight: '700', fontSize: '15px' },
  historyStatus: { fontSize: '13px', color: '#4CAF50' },
  historyName: { fontSize: '14px', marginBottom: '4px' },
  historyAddr: { fontSize: '13px', color: '#666', marginBottom: '4px' },
  historyTotal: { fontSize: '13px', color: '#888' },
};

const cs = {
  card: { margin: '0 12px 10px', backgroundColor: '#fff', borderRadius: '14px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  readyBanner: {
    backgroundColor: '#FFD600', margin: '-16px -16px 12px -16px', padding: '10px 16px',
    borderRadius: '14px 14px 0 0', textAlign: 'center', fontWeight: '800', fontSize: '14px',
    color: '#000', letterSpacing: '0.5px',
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  orderId: { fontSize: '16px', fontWeight: '800', marginRight: '8px' },
  statusBadge: { padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  orderTime: { fontSize: '13px', color: '#888' },
  clientBlock: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  clientName: { fontSize: '16px', fontWeight: '700' },
  callLink: { padding: '6px 14px', borderRadius: '20px', backgroundColor: '#E8F5E9', color: '#2E7D32', fontSize: '13px', fontWeight: '600', textDecoration: 'none' },
  addressBlock: { backgroundColor: '#F9F9F9', borderRadius: '10px', padding: '10px 12px', marginBottom: '10px' },
  addressText: { fontSize: '14px', color: '#333', lineHeight: '1.5' },
  zoneBadge: { display: 'inline-block', marginLeft: '8px', backgroundColor: '#E3F2FD', color: '#1565C0', padding: '2px 8px', borderRadius: '20px', fontSize: '11px' },
  mapsBtn: { padding: '7px 14px', borderRadius: '8px', border: '1px solid #E0E0E0', backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' },
  mapsButtons: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  mapLink: { padding: '7px 14px', borderRadius: '8px', backgroundColor: '#F5F5F5', color: '#333', fontSize: '13px', fontWeight: '600', textDecoration: 'none', display: 'inline-block' },
  items: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' },
  item: { backgroundColor: '#F0F0F0', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#555' },
  totalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  total: { fontSize: '18px', fontWeight: '800' },
  payment: { fontSize: '13px', color: '#888' },
  courierAssigned: { fontSize: '13px', color: '#666', marginBottom: '8px', fontStyle: 'italic' },
  actions: { display: 'flex', gap: '8px' },
  btnTake: { flex: 1, padding: '12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '700', fontFamily: 'inherit' },
  btnDone: { flex: 1, padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: '#4CAF50', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '700', fontFamily: 'inherit' },
  btnCancel: { padding: '12px 16px', borderRadius: '10px', border: '1px solid #E0E0E0', backgroundColor: '#fff', color: '#F44336', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit' },
  waitingMsg: { fontSize: '13px', color: '#999', textAlign: 'center', padding: '8px' },
};
